/* import __COLOCATED_TEMPLATE__ from './saved-messages.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { dropTask } from 'ember-concurrency';
import { isEmpty } from '@ember/utils';

const STATE_EMPTY = 'empty';
const STATE_SELECT = 'select';
const STATE_NEW_MESSAGE = 'newMessage';
const STATE_EDIT_MESSAGES = 'editMessages';
const STATE_MESSAGE_EDITOR = 'messageEditor';
const STATE_DELETE_MESSAGE = 'deleteMessage';

/**
 *
 * @param {string} state
 * @param {DS.Model|undefined} model
 * @returns {{model: DS.Model|undefined, state: string}}
 */
function createState(state, model) {
  return { state, model };
}

/**
 * Top level component for managing view state of the saved model component
 */
export default class SavedMessagesModal extends Component {
  @service('error-tracker') errorTracker;
  @service('commands.saved-message-commands') savedMessageCommandService;
  @service session;
  @service shData;

  @tracked nav = [];
  @tracked savedMessages;

  constructor() {
    super(...arguments);

    this.loadSavedMessages.perform();
  }

  @dropTask
  *loadSavedMessages() {
    let response;
    try {
      response = yield this.savedMessageCommandService.index(this.session.currentUser);
    } catch (e) {
      this.errorTracker.error(e, this);
    }

    this.shData.pushResponseToStore(response);
    this.savedMessages = this.shData.getModelArrayFromResponse(response, 'savedMessage');

    return this.savedMessages;
  }

  get isLoading() {
    return this.loadSavedMessages.isRunning;
  }

  @computed('nav.[]', 'savedMessages.[]')
  get view() {
    if (this.nav.length > 0) {
      return this.nav[this.nav.length - 1];
    }

    if (!isEmpty(this.savedMessages)) {
      return createState(STATE_SELECT);
    }

    return createState(STATE_EMPTY);
  }

  _pushState(state, model) {
    this.nav.push(createState(state, model));
    this.nav = this.nav;
  }

  _popState() {
    this.nav.pop();
    this.nav = this.nav;
  }

  _clearState() {
    this.nav = [];
  }

  @action
  createMessage({ title, body }) {
    return this.savedMessageCommandService.create(
      this.session.currentUser, { title, body }
    ).then(() => {
      this.loadSavedMessages.perform();
    }).then((response) => {
      this._popState();
    }).catch((e) => {
      this.errorTracker.error(e, this);
    });
  }

  @action
  confirmEdit(savedMessage, { title, body }) {
    return this.savedMessageCommandService.update(savedMessage, {
      title,
      body
    }).then(() => {
      this.loadSavedMessages.perform();
    }).then((response) => {
      this._popState();
    }).catch((e) => {
      this.errorTracker.error(e, this);
    });
  }

  @action
  confirmDelete(savedMessage) {
    return this.savedMessageCommandService.delete(
      savedMessage
    ).then(() => {
      this.loadSavedMessages.perform();
    }).then(() => {
      if (!isEmpty(this.savedMessages)) {
        this._popState();
      } else {
        this._clearState();
      }
    }).catch((e) => {
      this.errorTracker.error(e, this);
    });
  }

  @action
  addNewMessage() {
    this._pushState(STATE_NEW_MESSAGE);
  }

  @action
  showMessageEditor(message) {
    this._pushState(STATE_MESSAGE_EDITOR, message);
  }

  @action
  editMessages() {
    this._pushState(STATE_EDIT_MESSAGES);
  }

  @action
  deleteMessage(message) {
    this._pushState(STATE_DELETE_MESSAGE, message);
  }

  @action
  close() {
    this.onModalClose();
  }

  @action
  popState() {
    this._popState();
  }
}
